import React from 'react';

// Capture layout modelled on @incodetech/web 2.3.2's selfie Capture: a status
// ring around a circular camera crop with a head-and-shoulders outline. Only
// the visuals are ported — state handling stays in IncodeLivenessReactComponentV2.
// Shown when the `captureStyle` runtime flag is 'incode'.

export type RingState = 'idle' | 'loading' | 'capturing' | 'success' | 'error';

const RING_COLORS: Record<RingState, string> = {
    idle: '#c7ccd1',
    loading: '#2f6bff',
    capturing: '#1fa463',
    success: '#1fa463',
    error: '#d93636',
};

type CaptureSnapshot = {
    captureStatus?: string;
    detectionStatus?: string;
};

export function getRingState(capture: CaptureSnapshot, cameraLost = false): RingState {
    if (cameraLost) return 'error';
    switch (capture.captureStatus) {
        case 'initializing':
        case 'uploading':
            return 'loading';
        case 'capturing':
            return 'capturing';
        case 'success':
            return 'success';
        case 'uploadError':
            return 'error';
    }
    switch (capture.detectionStatus) {
        case 'getReady':
        case 'getReadyFinished':
            return 'loading';
        case 'capturing':
            return 'capturing';
        case 'success':
            return 'success';
        case 'error':
            return 'error';
        default:
            return 'idle';
    }
}

// Copied from FaceOutline in @incodetech/web 2.3.2 (ISC, Copyright (c) 2025-2026
// Incode Technologies, Inc.). Drawn in a 292x272 viewBox; the shoulders overflow
// it and are clipped by the circular crop.
const FACE_OUTLINE_PATH =
    'M145.633 8.0625C115.606 8.06268 85.6206 26.5984 76.9712 54.293L76.771 54.9492C70.4539 76.1063 75.0139 100.802 76.8687 110.58L77.3628 113.188L75.1528 114.656C66.1187 120.66 63.5861 124.089 62.8472 126.92C62.4669 128.377 62.4644 130.035 62.8003 132.412C63.1635 134.983 63.7533 137.484 64.4233 141.347L64.5142 141.841C65.5056 146.956 68.1062 152.133 71.2749 156.618C74.5527 161.257 78.2056 164.826 80.7349 166.584L82.4517 167.777V169.869C82.4517 177.866 85.3476 186.144 89.6763 194.128C93.9906 202.085 99.5748 209.473 104.623 215.646L105.526 216.751V218.178C105.526 220.891 105.68 223.73 105.801 226.9C105.92 230.011 106.003 233.347 105.828 236.771C105.475 243.652 104.077 251.044 99.7446 258.227C91.1977 272.397 72.1081 284.401 31.8677 291.776L29.936 292.124C-41.1597 304.684 -61.4563 361.617 -63.2153 392.062H145.633V400.062H-71.3638L-71.3462 396.044C-71.2048 364.597 -50.9804 298.295 28.5444 284.246L30.4448 283.904C69.9919 276.654 86.1642 265.253 92.894 254.095C96.3216 248.412 97.5261 242.442 97.8374 236.362C97.9939 233.307 97.9241 230.262 97.8071 227.206C97.7111 224.697 97.5799 222.105 97.5396 219.615C92.5931 213.5 87.048 206.064 82.644 197.941C78.308 189.944 74.9442 181.04 74.5024 171.896C71.3932 169.387 67.8531 165.638 64.7417 161.234C61.1656 156.173 57.9337 149.942 56.6587 143.353L56.5415 142.714C55.9864 139.514 55.2491 136.155 54.8784 133.531C54.4806 130.715 54.3377 127.843 55.106 124.899C56.5344 119.427 60.7885 114.886 68.5259 109.489C66.4692 98.2995 62.7062 74.0938 69.106 52.6602L69.3345 51.9092C79.2266 20.2173 112.853 0.0626788 145.633 0.0625V8.0625ZM145.635 0.0625C178.415 0.0625 212.042 20.2172 221.934 51.9092L222.163 52.6602C228.562 74.0938 224.799 98.2996 222.743 109.489C230.48 114.886 234.734 119.427 236.163 124.899C236.931 127.843 236.788 130.715 236.39 133.531C236.019 136.155 235.282 139.514 234.727 142.714L234.61 143.353C233.335 149.942 230.103 156.173 226.527 161.234C223.415 165.638 219.875 169.387 216.766 171.896C216.324 181.04 212.961 189.944 208.625 197.941C204.221 206.064 198.676 213.5 193.729 219.615C193.689 222.105 193.557 224.697 193.461 227.206C193.344 230.262 193.275 233.307 193.431 236.362C193.742 242.442 194.947 248.412 198.375 254.095C205.104 265.253 221.277 276.654 260.824 283.904L262.724 284.246C342.249 298.294 362.473 364.597 362.615 396.044L362.632 400.062H145.635V392.062H354.484C352.725 361.616 332.429 304.683 261.333 292.124L259.401 291.776C219.161 284.401 200.071 272.397 191.524 258.227C187.192 251.044 185.793 243.652 185.441 236.771C185.266 233.347 185.348 230.011 185.467 226.9C185.589 223.73 185.743 220.891 185.743 218.178V216.751L186.646 215.646C191.694 209.473 197.278 202.085 201.592 194.128C205.921 186.144 208.817 177.866 208.817 169.869V167.777L210.534 166.584C213.063 164.826 216.716 161.257 219.994 156.618C223.162 152.133 225.763 146.956 226.754 141.841L226.845 141.347C227.515 137.484 228.105 134.983 228.468 132.412C228.804 130.034 228.802 128.377 228.421 126.92C227.682 124.089 225.15 120.66 216.116 114.656L213.906 113.188L214.4 110.58C216.255 100.802 220.815 76.1064 214.498 54.9492L214.297 54.293C205.648 26.5983 175.662 8.0625 145.635 8.0625V0.0625Z';

type Props = {
    videoRef: React.RefObject<HTMLVideoElement | null>;
    ringState: RingState;
    mirrored: boolean;
    showOutline: boolean;
    children?: React.ReactNode;
};

const StyledCaptureFrame = ({ videoRef, ringState, mirrored, showOutline, children }: Props) => {
    const spinning = ringState === 'loading' || ringState === 'capturing';
    const ringStyle: React.CSSProperties = {
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        fill: 'none',
        transform: 'rotate(-90deg)',
    };

    return (
        <div
            data-testid="styled-capture-frame"
            data-ring-state={ringState}
            style={{
                position: 'relative',
                width: 'min(100%, 335px)',
                maxWidth: '50svh',
                aspectRatio: '1',
            }}
        >
            <style>{`
                @keyframes incode-ring-spin { from { transform: rotate(-90deg); } to { transform: rotate(270deg); } }
                @media (prefers-reduced-motion: reduce) {
                    .incode-ring-spinning { animation: none !important; }
                    .incode-ring-spinning circle { stroke-dasharray: none !important; }
                }
            `}</style>
            <svg style={ringStyle} viewBox="0 0 100 100" aria-hidden="true">
                <circle cx="50" cy="50" r="48.5" stroke={RING_COLORS.idle} strokeWidth="3" />
            </svg>
            <svg
                className={spinning ? 'incode-ring-spinning' : undefined}
                style={{ ...ringStyle, ...(spinning ? { animation: 'incode-ring-spin 1s linear infinite' } : null) }}
                viewBox="0 0 100 100"
                aria-hidden="true"
            >
                <circle
                    cx="50"
                    cy="50"
                    r="48.5"
                    pathLength={100}
                    stroke={RING_COLORS[ringState]}
                    strokeWidth="3"
                    strokeDasharray={spinning ? '28 72' : undefined}
                    strokeLinecap={spinning ? 'round' : undefined}
                    style={{ transition: 'stroke 0.6s ease-in-out' }}
                />
            </svg>
            <div
                style={{
                    position: 'absolute',
                    inset: '3.6%',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    clipPath: 'circle(50%)',
                    background: '#111',
                }}
            >
                <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    data-dd-privacy="hidden"
                    style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transform: mirrored ? 'scaleX(-1)' : 'none',
                    }}
                />
                {showOutline && (
                    <svg
                        aria-hidden="true"
                        viewBox="0 0 292 272"
                        fill="none"
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
                    >
                        <path d={FACE_OUTLINE_PATH} fill="#fff" opacity={0.8} />
                    </svg>
                )}
                {children}
            </div>
        </div>
    );
};

export default StyledCaptureFrame;
